import React, { useState, useEffect } from 'react';
import Spinner from '../components/Spinner';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../services/api';
import { useNotification } from '../components/Notification';
import { getCurrencySymbol } from '../utils/currency';
import './Inventory.css';
import './DetailPages.css';

function InventoryDetail({ tenantId }) {
  const navigate = useNavigate();
  const { showNotification } = useNotification();
  const { id } = useParams();
  const [item, setItem] = useState(null);
  const [location, setLocation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);
  const [priceData, setPriceData] = useState(null);
  const [loadingPrices, setLoadingPrices] = useState(false);

  useEffect(() => {
    if (tenantId && id) {
      loadData();
    }
  }, [tenantId, id]);

  const loadData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        loadTenant(),
        loadItem()
      ]);
    } finally {
      setLoading(false);
    }
  };

  const loadTenant = async () => {
    try {
      const data = await api.get('/tenants', null);
      const currentTenant = data.find(t => t.id === tenantId) || data[0];
      setTenant(currentTenant);
    } catch (error) {
      console.error('Error loading tenant:', error);
    }
  };

  const currency = getCurrencySymbol(tenant?.taxSystem || 'DE');

  const loadItem = async () => {
    try {
      const data = await api.get(`/inventory/${id}`, tenantId);
      setItem(data);
      if (data.locationId) {
        loadLocation(data.locationId);
      }
    } catch (error) {
      console.error('Error loading item:', error);
      showNotification('Fehler beim Laden des Inventargegenstands', 'error');
      navigate('/inventory');
    }
  };

  const loadLocation = async (locationId) => {
    try {
      const data = await api.get(`/locations/${locationId}`, tenantId);
      setLocation(data);
    } catch (error) {
      console.error('Error loading location:', error);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: currency === 'CHF' ? 'CHF' : 'EUR',
      minimumFractionDigits: 2
    }).format(amount);
  };

  const loadPrices = async () => {
    if (!item || !item.partNumber) {
      return;
    }

    setLoadingPrices(true);
    try {
      const data = await api.get(`/inventory/${id}/price-check`, tenantId);
      setPriceData(data);
    } catch (error) {
      console.error('Error loading prices:', error);
      setPriceData({ success: false, message: 'Fehler beim Abrufen der Preise' });
    } finally {
      setLoadingPrices(false);
    }
  };

  if (loading) {
    return <Spinner />;
  }

  if (!item) {
    return <div>Inventargegenstand nicht gefunden</div>;
  }

  const totalValue = (item.purchasePrice || 0) * (item.quantity || 0);
  const totalSellingValue = (item.sellingPrice || 0) * (item.quantity || 0);

  return (
    <div className="inventory-detail">
      <div className="detail-header">
        <button type="button" onClick={() => navigate('/inventory')} className="btn-secondary">
          ← Zurück
        </button>
        <h1>{item.name}</h1>
        <button type="button" onClick={() => navigate(`/inventory/${id}/edit`)} className="btn-primary">
          Bearbeiten
        </button>
      </div>

      <div className="detail-section">
        <h2>Allgemeine Informationen</h2>
        {item.description && <p><strong>Beschreibung:</strong> {item.description}</p>}
        {item.category && <p><strong>Kategorie:</strong> {item.category}</p>}
        {item.partNumber && (
          <p>
            <strong>Teilenummer:</strong> {item.partNumber}
            <a 
              href={`https://www.autodoc.de/search?keyword=${encodeURIComponent(item.partNumber)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-small"
              style={{ marginLeft: '1rem', display: 'inline-block', textDecoration: 'none' }}
            >
              Auf AutoDoc suchen →
            </a>
            <a 
              href={`https://www.ebay.de/sch/131090/i.html?_nkw=${encodeURIComponent(item.partNumber)}&_from=R40`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-small"
              style={{ marginLeft: '0.5rem', display: 'inline-block', textDecoration: 'none' }}
            >
              Auf eBay suchen →
            </a>
          </p>
        )}
        <p><strong>Menge:</strong> {item.quantity || 0} {item.unit || 'Stück'}</p>
      </div>

      {location && (
        <div className="detail-section">
          <h2>Standort</h2>
          <p>
            <span 
              className="link-button"
              onClick={() => navigate(`/locations/${location.id}`)}
            >
              {location.name}
            </span>
          </p>
        </div>
      )}

      <div className="detail-section">
        <h2>Preise</h2>
        <p><strong>Einkaufspreis:</strong> {formatCurrency(item.purchasePrice || 0)}</p>
        <p><strong>Verkaufspreis:</strong> {formatCurrency(item.sellingPrice || 0)}</p>
        <p><strong>Gesamtwert (Einkauf):</strong> {formatCurrency(totalValue)}</p>
        <p><strong>Gesamtwert (Verkauf):</strong> {formatCurrency(totalSellingValue)}</p>
        {item.sellingPrice > item.purchasePrice && (
          <p><strong>Möglicher Gewinn:</strong> {formatCurrency((item.sellingPrice - item.purchasePrice) * (item.quantity || 0))}</p>
        )}
        
        {item.partNumber && (
          <div style={{ marginTop: '1.5rem', padding: '1rem', border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f9f9f9' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <h3 style={{ margin: 0 }}>Marktpreise</h3>
              <button
                type="button"
                onClick={loadPrices}
                className="btn-small"
                disabled={loadingPrices}
              >
                {loadingPrices ? 'Lädt...' : 'Preise abrufen'}
              </button>
            </div>
            {priceData ? (
              <div>
                {priceData.ebay && (
                  <div style={{ marginBottom: '1rem', padding: '0.75rem', backgroundColor: '#fff', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <strong>eBay</strong>
                      {priceData.ebay.url && (
                        <a href={priceData.ebay.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.85em', color: '#2196f3' }}>
                          Auf eBay ansehen →
                        </a>
                      )}
                    </div>
                    {priceData.ebay.success ? (
                      <div>
                        <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#1e293b', marginBottom: '0.25rem' }}>
                          {formatCurrency(priceData.ebay.averagePrice || 0)}
                        </div>
                        <div style={{ fontSize: '0.9em', color: '#666' }}>
                          <div>Ø Preis: {formatCurrency(priceData.ebay.averagePrice || 0)}</div>
                          {priceData.ebay.medianPrice && (
                            <div>Median: {formatCurrency(priceData.ebay.medianPrice)}</div>
                          )}
                          <div>Min: {formatCurrency(priceData.ebay.minPrice || 0)}</div>
                          <div>Max: {formatCurrency(priceData.ebay.maxPrice || 0)}</div>
                          <div style={{ marginTop: '0.25rem', color: '#999' }}>
                            {priceData.ebay.count || 0} Angebote gefunden
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ color: '#d32f2f', fontSize: '0.9em' }}>
                        {priceData.ebay.message || 'Keine Preise gefunden'}
                      </div>
                    )}
                  </div>
                )}
                {priceData.autodoc && (
                  <div style={{ padding: '0.75rem', backgroundColor: '#fff', borderRadius: '4px', border: '1px solid #e0e0e0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                      <strong>AutoDoc</strong>
                      {priceData.autodoc.url && (
                        <a href={priceData.autodoc.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.85em', color: '#2196f3' }}>
                          Auf AutoDoc ansehen →
                        </a>
                      )}
                    </div>
                    {priceData.autodoc.success ? (
                      <div>
                        <div style={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#1e293b', marginBottom: '0.25rem' }}>
                          {formatCurrency(priceData.autodoc.averagePrice || 0)}
                        </div>
                        <div style={{ fontSize: '0.9em', color: '#666' }}>
                          <div>Ø Preis: {formatCurrency(priceData.autodoc.averagePrice || 0)}</div>
                          {priceData.autodoc.medianPrice && (
                            <div>Median: {formatCurrency(priceData.autodoc.medianPrice)}</div>
                          )}
                          <div>Min: {formatCurrency(priceData.autodoc.minPrice || 0)}</div>
                          <div>Max: {formatCurrency(priceData.autodoc.maxPrice || 0)}</div>
                          <div style={{ marginTop: '0.25rem', color: '#999' }}>
                            {priceData.autodoc.count || 0} Angebote gefunden
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div style={{ color: '#d32f2f', fontSize: '0.9em' }}>
                        {priceData.autodoc.message || 'Keine Preise gefunden'}
                      </div>
                    )}
                  </div>
                )}
                {!priceData.ebay?.success && !priceData.autodoc?.success && (
                  <div style={{ color: '#d32f2f', fontSize: '0.9em' }}>
                    Keine Preise gefunden. Bitte prüfen Sie die Teilenummer.
                  </div>
                )}
              </div>
            ) : (
              <div style={{ color: '#666', fontStyle: 'italic', fontSize: '0.9em' }}>
                Klicken Sie auf "Preise abrufen", um aktuelle Marktpreise von eBay und AutoDoc zu erhalten.
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default InventoryDetail;

